import * as React from 'react';

interface Section {
	type: string;
	items?: Array<{
		title: string;
		text: string;
	}>;
}

/**
 * Creates a FAQ schema from sections if an accordion section is present
 * @param sections Array of sections that may contain an accordion (FAQ) section
 * @returns FAQ schema object or null if no FAQ section is found
 */
export function createFaqSchema(sections?: Section[]) {
	if (!sections) return null;

	const faqSection = sections.find((section: Section) => section.type === 'accordion');
	if (!faqSection?.items?.length) return null;

	return {
		'@context': 'https://schema.org',
		'@type': 'FAQPage',
		mainEntity: faqSection.items.map((item) => ({
			'@type': 'Question',
			name: item.title,
			acceptedAnswer: {
				'@type': 'Answer',
				text: item.text,
			},
		})),
	};
}

/**
 * Renders FAQ schema as a script tag if FAQ data is present
 * @param sections Array of sections that may contain an accordion (FAQ) section
 * @returns Script element with FAQ schema or null
 */
export function FaqSchema({ sections }: { sections?: Section[] }) {
	const faqSchema = createFaqSchema(sections);
	if (!faqSchema) return null;

	return <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>;
}
